import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updatePassword, updateUserAttribute, confirmUserAttribute, fetchAuthSession } from 'aws-amplify/auth';
import { api, ApiError } from '../../lib/api/client';
import { AddressFields, addressComplete, emptyAddress, type Address } from './address';
import { DEFAULT_CONSENT_STATEMENT } from '../../lib/consent';
import { useSupport } from '../support/SupportWidget';
import { signOut as amplifySignOut } from 'aws-amplify/auth';

interface Profile { userId: string; name: string; email: string; role: string; deviceServicePort: number | null }
interface Account {
  name?: string;
  physicalAddress?: Address;
  billingAddress?: Address;
  rdOptOut?: boolean;
  storageActive?: boolean;
}

const ADMIN_ROLES = ['AccountAdmin', 'SiteAdmin'];

export function MyProfile() {
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => api.get<Profile>('/profile') });
  const account = useQuery({ queryKey: ['account'], queryFn: () => api.get<Account>('/account') });
  const isAdmin = ADMIN_ROLES.includes(profile.data?.role ?? '');

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>My Profile</h1>
          <div className="sub">Manage your name, sign-in email, password, scanner connection, and addresses.</div>
        </div>
      </div>

      <NameSection name={profile.data?.name ?? ''} loading={profile.isLoading} />
      <EmailSection email={profile.data?.email ?? ''} />
      <PasswordSection />
      <DevicePortSection port={profile.data?.deviceServicePort ?? null} loading={profile.isLoading} />
      <AddressSection account={account.data} />
      {isAdmin && <PrivacySection account={account.data} />}
      {isAdmin && account.data?.storageActive && <ConsentStatementSection />}
      {isAdmin && <DeleteAccountSection account={account.data} />}
    </div>
  );
}

/**
 * Danger zone — close the account permanently.
 *
 * Gated on AccountAdmin (the API enforces it too) and on typing the exact phrase, which is checked
 * server-side as well: the confirmation is a safety property, not a UI nicety, so it cannot be bypassed by
 * a script or a stale tab. The consequences are listed explicitly and specifically, because "are you
 * sure?" does not tell anyone what they are about to lose.
 */
function DeleteAccountSection({ account }: { account: Account | undefined }) {
  const CONFIRM = 'Delete Account';
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ subscriptionsCanceled: number; itemsDeleted: number } | null>(null);

  const del = useMutation({
    mutationFn: () => api.del<{ subscriptionsCanceled: number; itemsDeleted: number; errors: string[] }>(
      '/account', { confirm: typed.trim() },
    ),
    onSuccess: async (r) => {
      setErr(null);
      setDone(r);
      /*
       * Sign out immediately — the account and its Cognito identity are gone, so the session is a shell:
       * the token stays cryptographically valid until it expires, but every request behind it now resolves
       * to a user that no longer exists. Leaving the app up would show a logged-in UI failing on every
       * call. Local sign-out is enough and cannot meaningfully fail; a global sign-out would try to reach
       * a user Cognito no longer has.
       */
      try { await amplifySignOut(); } catch { /* tokens are being discarded anyway */ }
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'The account could not be deleted'),
  });

  if (done) {
    return (
      <>
        <div className="section-title" style={{ marginTop: 26 }}>Account closed</div>
        <div className="panel">
          <p style={{ marginTop: 0 }}>
            This account has been closed. {done.subscriptionsCanceled > 0
              ? `${done.subscriptionsCanceled} subscription${done.subscriptionsCanceled === 1 ? '' : 's'} cancelled and all data deleted.`
              : 'All data has been deleted.'}
          </p>
          <p className="field-help">You've been signed out. Thank you for using EFTCreator.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="section-title" style={{ marginTop: 26, color: 'var(--rose)' }}>Delete this account</div>
      <div className="panel danger-panel">
        <p style={{ marginTop: 0 }}>
          <strong>This permanently deletes {account?.name ?? 'this account'} and everything in it.</strong> It
          cannot be undone, and we cannot recover any of it for you afterwards.
        </p>
        <ul className="danger-list">
          <li><strong>Every subscription is cancelled immediately</strong> — no refund for the current period.</li>
          <li><strong>Every stored EFT and card conversion is deleted</strong>, including anything in Secure Storage. Download what you need first.</li>
          <li>All transaction history, drafts, devices, licenses, locations and audit records are deleted.</li>
          <li>All team members lose access, and their sign-ins are removed.</li>
          <li>Support tickets and their history are deleted.</li>
        </ul>
        <p className="field-help">
          If you only want to stop being billed, cancel your plans on <strong>Subscriptions &amp; billing</strong>
          instead — that keeps your data and your history.
        </p>

        {!open ? (
          <button className="btn btn-ghost btn-sm cancel-btn" onClick={() => { setErr(null); setOpen(true); }}>
            I understand — continue
          </button>
        ) : (
          <>
            <label className="field" style={{ maxWidth: 320, marginTop: 8 }}>
              <span className="field-label">Type <strong>{CONFIRM}</strong> to confirm</span>
              <input className="input" value={typed} autoComplete="off" placeholder={CONFIRM}
                onChange={(e) => { setTyped(e.target.value); setErr(null); }} />
            </label>
            {err && <p className="cart-error">{err}</p>}
            <div className="inline-form" style={{ marginTop: 12 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setTyped(''); setErr(null); }}>
                Keep my account
              </button>
              <button className="btn btn-primary btn-sm cancel-btn"
                disabled={typed.trim() !== CONFIRM || del.isPending}
                onClick={() => del.mutate()}>
                {del.isPending ? 'Deleting…' : 'Delete this account permanently'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function NameSection({ name, loading }: { name: string; loading: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(name);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setValue(name); }, [name]);

  const save = useMutation({
    mutationFn: () => api.patch('/profile', { name: value.trim() }),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['profile'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save your name'),
  });

  return (
    <>
      <div className="section-title">Your name</div>
      <div className="panel">
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field-label">Full name</span>
          <input className="input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. Jane Smith" disabled={loading} />
        </label>
        {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
        <button className="btn btn-primary btn-sm" style={{ marginTop: 14 }}
          disabled={save.isPending || value.trim() === name} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save name'}
        </button>
      </div>
    </>
  );
}

/**
 * The port this user's Device Service listens on.
 *
 * Configuration replaced a port scan: the app used to try twenty ports on every connect, which was slow and
 * became unworkable once the browser could hold the first request behind a local-network permission prompt.
 * Nearly everyone is on 8080 and leaves this alone; the field exists for the workstation where something else
 * had already taken the port.
 */
function DevicePortSection({ port, loading }: { port: number | null; loading: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setValue(port ? String(port) : ''); }, [port]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ deviceServicePort: number | null }>('/profile', {
        // Empty means "use the default" — sent as null so the stored override is removed rather than a copy
        // of the default being written.
        deviceServicePort: value.trim() === '' ? null : value.trim(),
      }),
    onSuccess: () => { setErr(null); setSaved(true); qc.invalidateQueries({ queryKey: ['profile'] }); },
    onError: (e) => { setSaved(false); setErr(e instanceof ApiError ? e.message : 'Could not save the port'); },
  });

  const dirty = (port ? String(port) : '') !== value.trim();

  return (
    <div className="panel">
      <div className="section-title" style={{ marginTop: 0 }}>Scanner connection</div>
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 0 }}>
        // The EFTCreator Device Service on your computer listens on <strong>port 8080</strong>. Leave this
        blank unless yours was set up on a different port — Device status can find it for you.
      </p>
      <div className="port-row">
        <label className="field" style={{ maxWidth: 200 }}>
          <span className="field-label">Device Service port</span>
          <input
            className="input"
            inputMode="numeric"
            placeholder="8080"
            value={value}
            onChange={(e) => { setValue(e.target.value.replace(/[^\d]/g, '').slice(0, 5)); setSaved(false); }}
            disabled={loading}
          />
        </label>
        <button className="btn btn-primary" disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save port'}
        </button>
        {value.trim() !== '' && (
          <button className="btn btn-ghost" disabled={save.isPending} onClick={() => { setValue(''); setSaved(false); }}>
            Use default
          </button>
        )}
      </div>
      {err && <p className="cart-error">{err}</p>}
      {saved && <p className="banner-ok">Saved. Reload any open capture tab to use it.</p>}
    </div>
  );
}

function EmailSection({ email }: { email: string }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<'view' | 'enter' | 'confirm'>('view');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function requestChange() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const res = await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: newEmail.trim().toLowerCase() } });
      if (res.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        setPhase('confirm');
        setMsg(`We sent a verification code to ${newEmail.trim()}.`);
      } else {
        await finishSync();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the email change');
    } finally { setBusy(false); }
  }

  async function confirm() {
    setBusy(true); setErr(null);
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code.trim() });
      await finishSync();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not verify the code');
    } finally { setBusy(false); }
  }

  async function finishSync() {
    // Refresh the token so it carries the new email, then sync it to our records.
    await fetchAuthSession({ forceRefresh: true });
    await api.post('/profile/email', {});
    qc.invalidateQueries({ queryKey: ['profile'] });
    qc.invalidateQueries({ queryKey: ['claims'] });
    setPhase('view'); setNewEmail(''); setCode(''); setMsg('Email updated.');
  }

  return (
    <>
      <div className="section-title" style={{ marginTop: 26 }}>Sign-in email</div>
      <div className="panel">
        {phase === 'view' && (
          <>
            <div className="addr-lines" style={{ marginBottom: 12 }}>{email || '—'}</div>
            {msg && <p className="banner-ok" style={{ marginBottom: 12 }}>{msg}</p>}
            <button className="btn btn-ghost btn-sm" onClick={() => { setPhase('enter'); setMsg(null); }}>Change email</button>
          </>
        )}
        {phase === 'enter' && (
          <>
            <label className="field" style={{ maxWidth: 420 }}>
              <span className="field-label">New email address</span>
              <input className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@example.com" autoFocus />
            </label>
            <p className="field-help" style={{ marginTop: 6 }}>You'll verify the new address with a code, and use it to sign in afterward.</p>
            {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="btn btn-primary btn-sm" disabled={busy || !newEmail.trim()} onClick={requestChange}>{busy ? 'Sending…' : 'Send code'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setPhase('view'); setErr(null); }}>Cancel</button>
            </div>
          </>
        )}
        {phase === 'confirm' && (
          <>
            {msg && <p className="banner-ok" style={{ marginBottom: 12 }}>{msg}</p>}
            <label className="field" style={{ maxWidth: 260 }}>
              <span className="field-label">Verification code</span>
              <input className="input mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus />
            </label>
            {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button className="btn btn-primary btn-sm" disabled={busy || !code.trim()} onClick={confirm}>{busy ? 'Verifying…' : 'Verify & update'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setPhase('view'); setErr(null); }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function PasswordSection() {
  const [oldPassword, setOld] = useState('');
  const [newPassword, setNew] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function change() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await updatePassword({ oldPassword, newPassword });
      setOld(''); setNew(''); setMsg('Password updated.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not change your password');
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="section-title" style={{ marginTop: 26 }}>Password</div>
      <div className="panel">
        {msg && <p className="banner-ok" style={{ marginBottom: 12 }}>{msg}</p>}
        <div className="address-grid" style={{ maxWidth: 520 }}>
          <label className="field"><span className="field-label">Current password</span>
            <input className="input" type="password" value={oldPassword} onChange={(e) => setOld(e.target.value)} autoComplete="current-password" /></label>
          <label className="field"><span className="field-label">New password</span>
            <input className="input" type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} autoComplete="new-password" /></label>
        </div>
        {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
        <button className="btn btn-primary btn-sm" style={{ marginTop: 14 }}
          disabled={busy || !oldPassword || newPassword.length < 8} onClick={change}>
          {busy ? 'Updating…' : 'Update password'}
        </button>
      </div>
    </>
  );
}

function AddressSection({ account }: { account: Account | undefined }) {
  const qc = useQueryClient();
  const [physical, setPhysical] = useState<Address>(emptyAddress());
  const [billing, setBilling] = useState<Address>(emptyAddress());
  const [billingSame, setBillingSame] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const p = account?.physicalAddress;
    const b = account?.billingAddress;
    setPhysical(p ? { ...emptyAddress(), ...p } : emptyAddress());
    setBilling(b ? { ...emptyAddress(), ...b } : emptyAddress());
    setBillingSame(Boolean(p && b && JSON.stringify(p) === JSON.stringify(b)));
  }, [account]);

  const save = useMutation({
    mutationFn: () => api.patch('/account', { physicalAddress: physical, billingAddress: billingSame ? physical : billing }),
    onSuccess: () => { setErr(null); setMsg('Addresses saved.'); qc.invalidateQueries({ queryKey: ['account'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save addresses'),
  });

  const canSave = addressComplete(physical) && (billingSame || addressComplete(billing));

  return (
    <>
      <div className="section-title" style={{ marginTop: 26 }}>Addresses</div>
      <div className="panel">
        {msg && <p className="banner-ok" style={{ marginBottom: 12 }}>{msg}</p>}
        <div className="field-label" style={{ marginBottom: 6 }}>Physical address</div>
        <AddressFields idPrefix="mp-phys" value={physical} onChange={(a) => { setPhysical(a); setMsg(null); }} />
        <label className="checkbox-row" style={{ marginTop: 16 }}>
          <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} />
          <span>Billing address is the same as the physical address</span>
        </label>
        {!billingSame && (
          <>
            <div className="field-label" style={{ margin: '16px 0 6px' }}>Billing address</div>
            <AddressFields idPrefix="mp-bill" value={billing} onChange={(a) => { setBilling(a); setMsg(null); }} />
          </>
        )}
        {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
        <button className="btn btn-primary btn-sm" style={{ marginTop: 18 }} disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save addresses'}
        </button>
      </div>
    </>
  );
}

/** Account-level privacy control: opt the account's stored data out of R&D / model-improvement use. */
function PrivacySection({ account }: { account: Account | undefined }) {
  const qc = useQueryClient();
  const [optOut, setOptOut] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setOptOut(Boolean(account?.rdOptOut)); }, [account]);

  const save = useMutation({
    mutationFn: (v: boolean) => api.patch('/account', { rdOptOut: v }),
    onSuccess: () => { setErr(null); setMsg('Preference saved.'); qc.invalidateQueries({ queryKey: ['account'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save your preference'),
  });

  return (
    <>
      <div className="section-title" style={{ marginTop: 26 }}>Data & privacy</div>
      <div className="panel">
        {msg && <p className="banner-ok" style={{ marginBottom: 12 }}>{msg}</p>}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={optOut}
            onChange={(e) => { setOptOut(e.target.checked); setMsg(null); save.mutate(e.target.checked); }}
          />
          <span>
            <strong>Do not use my account's data for research &amp; development.</strong>
            <br />
            <span className="field-help">
              When checked, we exclude your account's stored data (including biometrics) from internal
              research, development, and model-improvement use. We still process your data as needed to
              provide the service. See our{' '}
              <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </span>
          </span>
        </label>
        {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
      </div>
    </>
  );
}

/**
 * The biometric-consent statement, READ-ONLY.
 *
 * This was a free-text editor. It shouldn't be: the statement is the legal disclosure a subject
 * acknowledges before we retain their fingerprints, and letting a customer write their own means
 * biometric data gets collected under wording nobody reviewed — weaker wording exposes them and us.
 * Customers now see exactly what their subjects will see; a bespoke statement is arranged through support
 * and set by Lakota staff after review (PATCH /admin/accounts/{id}).
 */
function ConsentStatementSection() {
  const { openSupport } = useSupport();
  return (
    <>
      <div className="section-title" style={{ marginTop: 26 }}>Biometric-consent statement</div>
      <div className="panel">
        <p className="field-help" style={{ marginTop: 0, marginBottom: 12 }}>
          Because your account stores fingerprint data, we require a consent acknowledgment before each
          capture. This is the exact wording your subjects (live scan) or operators (card conversion)
          confirm, and the version in force is recorded on every transaction. It is the same statement for
          every EFTCreator customer.
        </p>
        <div className="consent-statement">{DEFAULT_CONSENT_STATEMENT}</div>
        <p className="field-help" style={{ marginBottom: 0 }}>
          Questions about this wording, or a requirement your jurisdiction imposes?{' '}
          <button type="button" className="linklike" onClick={openSupport}>Ask the assistant</button>.
        </p>
      </div>
    </>
  );
}
